import { BeforeRecordingModal } from "@/components/BeforeRecordingModal";
import { useAppModal } from "@/contexts/AppModal/useAppModal";
import { useRecordingStatus } from "@/contexts/RecordingStatus/useRecordingStatus";
import { Recording, RecordingCreationType } from "@/models/recording";
import patchRecordingData from "@/services/patchRecordingData";
import { ButtonSize, MicOnIcon } from "@enxcs/dap-ui-stratos";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RecordStatusEnum } from "../../types";
import FailActions from "../FailActions";
import FailActionsIndexedDB from "../FailActionsIndexedDB";
import FailActionsUpload from "../FailActionsUpload";
import RecordsActions from "../RecordsActions";
import { ActionContainer, ReadyToRecordButton, ResumeIconButton } from "./styles";

interface RecordStatusActionsProps {
  record: Recording;
  onOpenExportModal: (recordingId: string) => void;
}

const RecordStatusActions = ({ record, onOpenExportModal }: RecordStatusActionsProps) => {
  const { recordingId, status, isUploadInterrupted, isOnIndexDB, isFailedDifferentHash, recordingType } = record;

  const { lang } = useParams();

  const { onOpenModal, onCloseModal } = useAppModal();

  const readyStatuses = new Set([RecordStatusEnum.DONE, RecordStatusEnum.READY_TO_EDIT, RecordStatusEnum.READY_TO_EXPORT, RecordStatusEnum.GENERATED]);
  const isPaused = status === RecordStatusEnum.PAUSED;
  const hasFailed = status === RecordStatusEnum.FAILED;
  const isReadyForRecording = status === RecordStatusEnum.READY_FOR_RECORDING;
  const isStatusRecording = status === RecordStatusEnum.RECORDING;

  const { isRecording } = useRecordingStatus();

  const navigate = useNavigate();

  // Temporary solution to force Pause Recording
  useEffect(() => {
    if (isOnIndexDB && isStatusRecording && !isRecording) {
      patchRecordingData({ recordingId, status: RecordStatusEnum.PAUSED });
    }
  }, [isOnIndexDB, isRecording, isStatusRecording, recordingId]);

  if (isUploadInterrupted) {
    return (
      <ActionContainer>
        <FailActionsIndexedDB recording={record} />
      </ActionContainer>
    );
  }

  if (isPaused) {
    return (
      <ActionContainer>
        <ResumeIconButton
          data-qa-id="edit-recording-play-pause-button"
          icon={MicOnIcon}
          title={""}
          disabled={isRecording || !isOnIndexDB}
          size={ButtonSize.S}
          onClick={() => navigate(`/${lang}/recording/${recordingId}`)}
        />
      </ActionContainer>
    );
  }

  if (isReadyForRecording) {
    return (
      <ActionContainer>
        <ReadyToRecordButton
          data-qa-id="ready-for-recording-button"
          icon={MicOnIcon}
          title={""}
          disabled={isRecording}
          size={ButtonSize.S}
          onClick={() => onOpenModal(<BeforeRecordingModal onClose={onCloseModal} recordingUUID={record.recordingId} />)}
        />
      </ActionContainer>
    );
  }

  if (readyStatuses.has(status)) {
    return (
      <RecordsActions
        record={record}
        onUpload={() => {
          onOpenExportModal(record.recordingId);
        }}
      />
    );
  }

  if (hasFailed && isFailedDifferentHash && recordingType === RecordingCreationType.UPLOADED_RECORDING) {
    return <FailActionsUpload record={record} />;
  }

  if (hasFailed) {
    return <FailActions record={record} />;
  }

  return null;
};

export default RecordStatusActions;
